import { call, put, select, takeEvery } from 'redux-saga/effects';

import { MAX_PAYLOAD_SIZE } from '../../../config/response-size-limit';
import { challengeTypes } from '../../utils/challenge-types';
import { createFlashMessage } from '../components/Flash/redux';
import { FlashMessages } from '../components/Flash/redux/flash-messages';
import {
  challengeDataSelector,
  challengeMetaSelector
} from '../templates/Challenges/redux/selectors';
import { mapFilesToChallengeFiles, postSaveChallenge } from '../utils/ajax';
import {
  bodySizeFits,
  getStringSizeInBytes,
  standardizeRequestBody
} from '../utils/challenge-request-helpers';
import { saveChallengeComplete } from './actions';
import { savedChallengesSelector } from './selectors';

function* saveChallengeSaga() {
  const { id, challengeType } = yield select(challengeMetaSelector);
  const { challengeFiles } = yield select(challengeDataSelector);
  const savedChallenges = yield select(savedChallengesSelector);
  const savedChallenge = savedChallenges.find(challenge => challenge.id === id);

  // don't let users save more than once every 5 seconds
  if (Date.now() - savedChallenge?.lastSavedDate < 5000) {
    return yield put(
      createFlashMessage({
        type: 'danger',
        message: FlashMessages.CodeSaveLess
      })
    );
  }

  // only allow saving of multifileCertProject's
  if (challengeType === challengeTypes.multifileCertProject) {
    const body = standardizeRequestBody({ id, challengeFiles, challengeType });
    const bodySizeInBytes = getStringSizeInBytes(body);

    if (!bodySizeFits(bodySizeInBytes)) {
      return yield put(
        createFlashMessage({
          type: 'danger',
          message: FlashMessages.ChallengeSaveTooBig,
          variables: {
            'max-size': MAX_PAYLOAD_SIZE,
            'user-size': bodySizeInBytes
          }
        })
      );
    } else {
      try {
        const { data } = yield call(postSaveChallenge, body);

        if (
          data?.type === 'error' &&
          data?.code === 'PAYLOAD_SIZE_LIMIT_EXCEEDED'
        ) {
          yield put(
            createFlashMessage({
              type: 'danger',
              message: FlashMessages.PayloadSizeLimitExceeded,
              variables: {
                'current-size': (data.details?.currentSize / 1024).toFixed(2),
                'max-size': (data.details?.maxAllowed / 1024).toFixed(0)
              }
            })
          );
        } else if (data?.message) {
          yield put(createFlashMessage(data));
        } else if (data?.savedChallenges) {
          yield put(
            saveChallengeComplete(
              mapFilesToChallengeFiles(data.savedChallenges)
            )
          );
          yield put(
            createFlashMessage({
              type: 'success',
              message: FlashMessages.CodeSaved
            })
          );
        }
      } catch (e) {
        yield put(
          createFlashMessage({
            type: 'danger',
            message: FlashMessages.CodeSaveError
          })
        );
      }
    }
  }
}

export function createSaveChallengeSaga(types) {
  return [takeEvery(types.saveChallenge, saveChallengeSaga)];
}
